import { useState } from 'react'
import { STATUS_STYLE } from './constants'
import { WifiIcon } from './Icons'

// ── Live Payload Modal ─────────────────────────────────────────────────────────
const LivePayloadModal = ({ controller, livePayload, onClose }) => {
  const payload = livePayload?.payload || null
  const receivedAt = livePayload?.receivedAt || null
  const paramMappings = livePayload?.paramMappings || {}
  const dataFields = payload?.Data ? Object.entries(payload.Data) : []

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
      <div className='bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col'>
        {/* Modal Header */}
        <div className='flex items-center justify-between px-5 py-4 border-b border-gray-100'>
          <div>
            <h2 className='text-sm font-bold text-gray-800'>
              {controller.name}
            </h2>
            <p className='text-[11px] text-gray-400 mt-0.5 font-mono'>
              {controller.topic || '—'}
            </p>
          </div>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 transition-colors'
          >
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className='overflow-y-auto px-5 py-4 space-y-4 flex-1'>
          {!payload ? (
            <div className='py-10 text-center text-gray-400 text-sm'>
              <svg
                className='w-10 h-10 mx-auto mb-3 text-gray-200'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z'
                />
              </svg>
              <p className='font-medium'>No live data yet</p>
              <p className='text-xs mt-1'>
                Waiting for MQTT message on this topic
              </p>
            </div>
          ) : (
            <>
              {/* Device Info */}
              <div className='grid grid-cols-2 gap-2 text-xs'>
                {[
                  ['Device Status', payload.Device_status],
                  ['Timestamp', payload.Timestamp],
                  ['MAC ID', payload.MAC_ID],
                  ['Firmware', payload.Firmware_ver],
                  ['Client ID', payload.Client_ID],
                  ['Count', payload.Count]
                ].map(([label, val]) =>
                  val ? (
                    <div
                      key={label}
                      className='bg-gray-50 rounded-lg px-3 py-2'
                    >
                      <p className='text-[10px] text-gray-400 uppercase tracking-wide'>
                        {label}
                      </p>
                      <p className='font-mono font-semibold text-gray-700 mt-0.5 break-all'>
                        {val}
                      </p>
                    </div>
                  ) : null
                )}
              </div>

              {/* Data Fields */}
              {dataFields.length > 0 && (
                <div>
                  <p className='text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2'>
                    Sensor Data
                  </p>
                  <div className='grid grid-cols-3 gap-2'>
                    {dataFields.map(([key, val]) => {
                      const mapping =
                        paramMappings[key.toLowerCase()] || paramMappings[key]
                      const label = mapping?.display_name || key.toUpperCase()
                      const unit = mapping?.unit || ''
                      return (
                        <div
                          key={key}
                          className='bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-center'
                        >
                          <p
                            className='text-[10px] text-blue-400 uppercase tracking-wide truncate'
                            title={label}
                          >
                            {label}
                          </p>
                          <p className='font-mono font-bold text-blue-700 text-sm mt-0.5'>
                            {val ?? '—'}
                            {unit && (
                              <span className='text-[10px] font-normal text-blue-400 ml-0.5'>
                                {unit}
                              </span>
                            )}
                          </p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className='px-5 py-3 border-t border-gray-100 flex items-center justify-between'>
          <span className='text-[10px] text-gray-400'>
            {receivedAt
              ? `Last received: ${new Date(receivedAt).toLocaleTimeString(
                  undefined,
                  {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  }
                )}`
              : 'No data received'}
          </span>
          <button
            onClick={onClose}
            className='text-xs px-4 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

const normalizeStatus = status => {
  if (!status) return 'offline'
  return status.toLowerCase() === 'offline' ? 'offline' : 'online'
}

const getStatusIcon = status => {
  return status === 'online'
    ? <WifiIcon status='online' className='text-green-500' />
    : <WifiIcon status='offline' />
}

// ── Controller Card ────────────────────────────────────────────────────────────
const ControllerCard = ({
  controller,
  livePayload,
  onEdit,
  onDelete,
  onSetParams,
  onToggleActive,
  canWrite = true,
}) => {
  const [copied, setCopied] = useState(null)
  const [showPayload, setShowPayload] = useState(false)

  // Admin enable flag — defaults to active when absent (older payloads)
  const isActive = controller.is_active !== false

  const statusRaw = normalizeStatus(
    livePayload?.status || controller.status || 'offline'
  )

  const statusLabel = statusRaw === 'online' ? 'Online' : 'Offline'

  const cfg = STATUS_STYLE[statusRaw] || STATUS_STYLE.offline

  // Parse 4-level hierarchy from the stored topic (most accurate source)
  // Format: client_id/location_id/asset_id/device_id
  const topicParts = controller.topic ? controller.topic.split('/') : []
  const clientId   = topicParts[0] || null
  const locationId = topicParts[1] || null
  const assetId    = topicParts[2] || controller.asset_names?.[0] || null
  const deviceId   = controller.device_id || topicParts[3] || null

  const copyToClipboard = (key, value) => {
    if (!value) return
    navigator.clipboard.writeText(value).then(() => {
      setCopied(key)
      setTimeout(() => setCopied(null), 1200)
    })
  }

  const isOnline = statusRaw === 'online'
  const hasData = !!livePayload?.payload

  return (
    <>
      <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow duration-200'>
        {/* Header */}
        <div className='flex items-start justify-between'>
          <div className='flex items-center gap-2.5'>
            {getStatusIcon(statusRaw)}
            <div>
              <p className='font-semibold text-gray-800 text-sm'>
                {controller.name}
              </p>
              {assetId && (
                <span className='inline-block mt-0.5 text-[10px] font-mono text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5'>
                  {assetId}
                </span>
              )}
            </div>
          </div>

          {/* Status badge */}
          <div className='flex items-center gap-1.5'>
            {!isActive ? (
              <span className='text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-gray-200 text-gray-600'>
                Inactive
              </span>
            ) : (
              <>
                {isOnline && (
                  <span className='relative flex h-2 w-2'>
                    <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75' />
                    <span className='relative inline-flex rounded-full h-2 w-2 bg-green-500' />
                  </span>
                )}
                <span
                  className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${cfg.badge}`}
                >
                  {statusLabel}
                </span>
              </>
            )}
          </div>
        </div>

        {/* ── Topic (prominent) ────────────────────────────────────── */}
        {controller.topic ? (
          <div className='bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5'>
            <div className='flex items-center justify-between mb-1'>
              <span className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide'>
                MQTT Topic
              </span>
              <button
                onClick={() => copyToClipboard('topic', controller.topic)}
                className='text-[10px] text-blue-600 hover:text-blue-800 font-medium'
              >
                {copied === 'topic' ? 'Copied ✓' : 'Copy'}
              </button>
            </div>
            <p className='font-mono text-xs text-gray-700 break-all leading-relaxed'>
              {controller.topic}
            </p>
          </div>
        ) : (
          <div className='flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5'>
            <svg className='w-3.5 h-3.5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2}
                d='M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' />
            </svg>
            No topic — assign an asset to generate MQTT topic
          </div>
        )}

        {/* ── Information panel ─────────────────────────────────────── */}
        <div className='grid grid-cols-2 gap-2 text-xs'>
          {controller.location_name && (
            <div className='bg-emerald-50 border border-emerald-100 rounded px-2.5 py-1.5'>
              <p className='text-[10px] text-emerald-500 uppercase tracking-wide font-medium'>Location</p>
              <p className='text-emerald-800 font-semibold truncate'>{controller.location_name}</p>
            </div>
          )}
          {(controller.broker_host || controller.broker_port) && (
            <div className='bg-gray-50 border border-gray-200 rounded px-2.5 py-1.5'>
              <p className='text-[10px] text-gray-400 uppercase tracking-wide font-medium'>Broker</p>
              <p className='font-mono text-gray-700 text-[11px] truncate'>
                {controller.broker_host || '—'}:{controller.broker_port ?? 1883}
              </p>
            </div>
          )}
        </div>

        {/* IDs — 4-level MQTT hierarchy */}
        {(clientId || locationId || assetId || deviceId) && (
          <div className='text-xs space-y-1.5'>
            {[
              { key: 'client',   label: 'Client ID',                              value: clientId   },
              { key: 'location', label: 'Location ID',                            value: locationId },
              { key: 'asset',    label: controller.asset_names?.[0] || 'Asset',   value: assetId    },
              { key: 'device',   label: 'Device ID',                              value: deviceId   },
            ].map(({ key, label, value }) =>
              value ? (
                <div key={key} className='flex items-center justify-between bg-gray-50 px-2 py-1 rounded'>
                  <span className='text-gray-500 text-[10px] uppercase tracking-wide w-24 flex-shrink-0 truncate' title={label}>{label}</span>
                  <button
                    onClick={() => copyToClipboard(key, value)}
                    className='flex items-center gap-2 font-mono text-[11px] text-blue-700 hover:text-blue-900 min-w-0'
                  >
                    <span className='truncate max-w-[140px]'>{value}</span>
                    <span className='text-[9px] flex-shrink-0'>
                      {copied === key ? '✓' : 'Copy'}
                    </span>
                  </button>
                </div>
              ) : null
            )}
          </div>
        )}

        {/* Last Seen */}
        {/* {lastSeen && (
          <p className="text-[10px] text-gray-400">Last seen: {lastSeen}</p>
        )} */}

        {/* Actions */}
        <div className='flex items-center justify-between pt-2 border-t border-gray-50 text-xs'>
          <button
            onClick={() => setShowPayload(true)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
              isOnline
                ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                : hasData
                ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <svg
              className='w-3.5 h-3.5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>

            {isOnline ? 'Live Data' : hasData ? 'Data Available' : 'No Data'}
          </button>

          {/* Edit / Params / Delete — write roles only */}
          <div className='flex items-center gap-4'>
            {canWrite && onToggleActive && (
              <button
                onClick={() => onToggleActive(controller)}
                title={isActive ? 'Deactivate controller' : 'Activate controller'}
                className='flex items-center gap-1.5'
              >
                <span
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    isActive ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      isActive ? 'translate-x-4' : 'translate-x-1'
                    }`}
                  />
                </span>
              </button>
            )}
            {canWrite && (
              <button
                onClick={() => onEdit(controller)}
                className='flex items-center gap-1.5 text-blue-600 hover:text-blue-800 transition-colors'
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.8}
                    d='M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z' />
                </svg>
                Edit
              </button>
            )}
            <button
              onClick={() => onSetParams(controller)}
              className='flex items-center gap-1.5 text-teal-600 hover:text-teal-800 transition-colors'
            >
              <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.8}
                  d='M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z' />
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.8}
                  d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
              </svg>
              Params
            </button>
            {canWrite && (
              <button
                onClick={() => onDelete(controller)}
                className='flex items-center gap-1.5 text-red-500 hover:text-red-700 transition-colors'
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.8}
                    d='M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916' />
                </svg>
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {showPayload && (
        <LivePayloadModal
          controller={controller}
          livePayload={livePayload}
          onClose={() => setShowPayload(false)}
        />
      )}
    </>
  )
}

export default ControllerCard
